import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  Package, 
  Users, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Bus,
  Wrench,
  CreditCard
} from 'lucide-react';
import { ventasApi, busesApi, mecanicosApi, mantenimientosApi, pagosMantenimientoApi, dashboardExtraApi } from '../lib/api';
import { ventaPagada } from '../lib/ventas';
import MarcaPersonal from '../components/MarcaPersonal';
import { fechaLima } from '../lib/fechas';

const MESES_DASH = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
function etiquetaPeriodoDash(periodo: string) {
  const [anio, mes, rango] = periodo.split('-');
  const [inicio, fin] = rango.split('/');
  return `${parseInt(inicio, 10)}-${fin === 'fin' ? 'Fin de mes' : fin} ${MESES_DASH[parseInt(mes, 10) - 1] || mes} ${anio}`;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalVentas: 0,
    busesActivos: 0,
    busesMantenimiento: 0,
    mecanicosDisponibles: 0,
    ventasPendientes: 0,
    ventasCompletadas: 0,
    asistenciaHoy: 0,
    porcentajeAsistencia: 0,
    alertasCriticas: 0
  });

  const [alertasMantenimiento, setAlertasMantenimiento] = useState<any[]>([]);
  const [ventasRecientes, setVentasRecientes] = useState<any[]>([]);
  const [buses, setBuses] = useState<any[]>([]);
  const [pagosMantPendientes, setPagosMantPendientes] = useState<{ periodo: string; monto: number; pendientes: any[] }>({ periodo: '', monto: 280, pendientes: [] });

  const [productosMasVendidos, setProductosMasVendidos] = useState<any[]>([]);

  useEffect(() => {
    dashboardExtraApi.getProductosMasVendidos(10).then(setProductosMasVendidos).catch(() => setProductosMasVendidos([]));
  }, []);

  useEffect(() => {
    cargarEstadisticas();
    cargarVentasRecientes();
    cargarBuses();
    calcularAlertasMantenimiento();
    cargarPagosMantenimientoPendientes();

    // Sincronización periódica (ya no hay suscripción realtime de Supabase)
    const intervalo = setInterval(() => {
      cargarEstadisticas();
      cargarVentasRecientes();
      cargarBuses();
      calcularAlertasMantenimiento();
      cargarPagosMantenimientoPendientes();
    }, 30000);

    return () => clearInterval(intervalo);
  }, []);

  // Función para sanitizar asistencias (igual que en MecanicosCompleto)
  const sanitizarAsistencias = (asistencias: any): { fecha: string; presente: boolean; estado?: string }[] => {
    if (!asistencias) return [];
    if (Array.isArray(asistencias)) {
      // Asegurar retrocompatibilidad agregando 'estado' si no existe
      return asistencias.map(a => ({
        fecha: a.fecha,
        presente: a.presente,
        estado: a.estado || (a.presente ? 'presente' : 'ausente')
      }));
    }
    if (typeof asistencias === 'string') {
      try {
        const parsed = JSON.parse(asistencias);
        if (Array.isArray(parsed)) {
          return parsed.map(a => ({
            fecha: a.fecha,
            presente: a.presente,
            estado: a.estado || (a.presente ? 'presente' : 'ausente')
          }));
        }
        return [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const cargarEstadisticas = async () => {
    try {
      // Cargar ventas
      const ventas = await ventasApi.getAll();

      const totalVentas = ventas?.reduce((sum: number, venta: any) => sum + (venta.total || 0), 0) || 0;
      // Pendiente = venta a credito con saldo por cobrar (ventas.estado no cambia al pagarse)
      const ventasPendientes = ventas?.filter((v: any) => ((Number(v.saldo_credito_productos) || 0) + (Number(v.saldo_credito_servicios) || 0)) > 0.004).length || 0;
      const ventasCompletadas = ventas?.filter((v: any) => ventaPagada(v)).length || 0;

      // Cargar buses
      const buses = await busesApi.getAll();

      const busesActivos = buses?.filter((bus: any) => bus.estado === 'activo').length || 0;
      const busesMantenimiento = buses?.filter((bus: any) => bus.estado === 'mantenimiento').length || 0;

      // Cargar mecánicos con sus asistencias
      const mecanicos = await mecanicosApi.getAll();

      // Filtrar mecánicos activos (usar 'estado' en lugar de 'activo')
      const mecanicosActivos = mecanicos?.filter((m: any) => m.estado === 'activo') || [];

      // Calcular asistencia del día actual
      const fechaHoy = fechaLima(new Date());
      const asistenciaHoy = mecanicosActivos.filter(mecanico => {
        const asistencias = sanitizarAsistencias(mecanico.asistencias);
        return asistencias.some(a => a.fecha === fechaHoy && a.estado === 'presente');
      }).length;

      // Calcular mecánicos disponibles para trabajar hoy (excluyendo descansos)
      const mecanicosDisponiblesHoy = mecanicosActivos.filter(mecanico => {
        const asistencias = sanitizarAsistencias(mecanico.asistencias);
        const asistenciaHoy = asistencias.find(a => a.fecha === fechaHoy);
        return !asistenciaHoy || (asistenciaHoy.estado !== 'descanso' && asistenciaHoy.estado !== 'estudio');
      }).length;

      const porcentajeAsistencia = mecanicosDisponiblesHoy > 0 
        ? Math.round((asistenciaHoy / mecanicosDisponiblesHoy) * 100)
        : 0;

      setStats(prevStats => ({
        ...prevStats,
        totalVentas,
        busesActivos,
        busesMantenimiento,
        mecanicosDisponibles: mecanicosDisponiblesHoy, // Mecánicos disponibles para trabajar hoy
        ventasPendientes,
        ventasCompletadas,
        asistenciaHoy,
        porcentajeAsistencia
      }));
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const cargarVentasRecientes = async () => {
    try {
      const ventas = await ventasApi.getAll();
      const recientes = [...(ventas || [])]
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 5);
      setVentasRecientes(recientes);
    } catch (error) {
      console.error('Error cargando ventas recientes:', error);
    }
  };

  const cargarBuses = async () => {
    try {
      const data = await busesApi.getAll();
      setBuses(data || []);
    } catch (error) {
      console.error('Error cargando buses:', error);
    }
  };

  const cargarPagosMantenimientoPendientes = async () => {
    try {
      const data = await pagosMantenimientoApi.getPendientes();
      setPagosMantPendientes(data);
    } catch (error) {
      console.error('Error cargando pagos de mantenimiento pendientes:', error);
    }
  };

  const calcularAlertasMantenimiento = async () => {
    try {
      // Cargar mantenimientos programados que no estén completados, con su bus asociado
      const [todosMantenimientos, todosBuses] = await Promise.all([
        mantenimientosApi.getAll(),
        busesApi.getAll()
      ]);
      const busesPorId = new Map(todosBuses.map((b: any) => [b.id, b]));
      const mantenimientos = (todosMantenimientos || [])
        .filter((m: any) => ['programado', 'pendiente', 'en_proceso'].includes(m.estado))
        .map((m: any) => ({ ...m, buses: busesPorId.get(m.bus_id) }))
        .sort((a: any, b: any) => new Date(a.fecha_programada).getTime() - new Date(b.fecha_programada).getTime());

      if (!mantenimientos) return;

      const alertas: any[] = [];
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      
      mantenimientos.forEach((mant: any) => {
        if (!mant.buses || !mant.fecha_programada) return;
        
        const fechaProgramada = new Date(mant.fecha_programada);
        fechaProgramada.setHours(0, 0, 0, 0);
        
        const diasDiferencia = Math.ceil((fechaProgramada.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
        
        let prioridad = 'baja';
        let mensaje = '';
        let icono = 'clock';
        
        if (diasDiferencia < 0) {
          prioridad = 'critica';
          mensaje = `¡VENCIDO! ${Math.abs(diasDiferencia)} días de retraso`;
          icono = 'alert';
        } else if (diasDiferencia === 0) {
          prioridad = 'critica';
          mensaje = '¡HOY! Mantenimiento programado para hoy';
          icono = 'alert';
        } else if (diasDiferencia <= 3) {
          prioridad = 'alta';
          mensaje = `Urgente: en ${diasDiferencia} día${diasDiferencia > 1 ? 's' : ''}`;
          icono = 'alert-circle';
        } else if (diasDiferencia <= 7) {
          prioridad = 'media';
          mensaje = `Próximo: en ${diasDiferencia} días`;
          icono = 'clock';
        } else if (diasDiferencia <= 15) {
          prioridad = 'media';
          mensaje = `Planificado: en ${diasDiferencia} días`;
          icono = 'clock';
        }
        
        if (prioridad !== 'baja') {
          alertas.push({
            id: mant.id,
            titulo: mant.titulo || 'Mantenimiento',
            tipo: mant.tipo || 'preventivo',
            categoria: mant.categoria || 'general',
            descripcion: mant.descripcion,
            bus: mant.buses.placa,
            busPadron: mant.buses.padron || mant.buses.numero,
            busModelo: `${mant.buses.marca} ${mant.buses.modelo}`,
            fechaProgramada: mant.fecha_programada,
            estado: mant.estado,
            diasDiferencia,
            prioridad,
            mensaje,
            icono
          });
        }
      });
      
      // Ordenar por prioridad (crítica > alta > media) y luego por fecha
      alertas.sort((a, b) => {
        const prioridadOrder = { critica: 3, alta: 2, media: 1 };
        const diffPrioridad = prioridadOrder[b.prioridad as keyof typeof prioridadOrder] - prioridadOrder[a.prioridad as keyof typeof prioridadOrder];
        if (diffPrioridad !== 0) return diffPrioridad;
        return a.diasDiferencia - b.diasDiferencia;
      });
      
      setAlertasMantenimiento(alertas);
      
      // Contar alertas críticas para la tarjeta superior
      const alertasCriticas = alertas.filter(alerta => alerta.prioridad === 'critica').length;
      
      // Actualizar el estado con las alertas críticas
      setStats(prevStats => ({
        ...prevStats,
        alertasCriticas
      }));
    } catch (error) {
      console.error('Error calculando alertas:', error);
    }
  };

  // Función para iniciar un mantenimiento
  const iniciarMantenimiento = async (mantenimientoId: string) => {
    try {
      await mantenimientosApi.update(mantenimientoId, { estado: 'en_proceso' });

      alert('✅ Mantenimiento iniciado correctamente');
      calcularAlertasMantenimiento();
    } catch (error) {
      console.error('Error al iniciar mantenimiento:', error);
      alert('❌ Error al iniciar mantenimiento');
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle }: {
    title: string;
    value: string | number;
    icon: any;
    color: string;
    subtitle?: string;
  }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
        <Icon className={`h-8 w-8 ${color}`} />
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard - Sistema Mantsila</h1>
        <div className="text-sm text-gray-500">
          Última actualización: {new Date().toLocaleTimeString()}
        </div>
      </div>

      {/* Alerta de buses con deuda pendiente (credito sin limite, solo aviso) */}
      {buses.filter(b => (b.credito_usado || 0) > 0).length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center">
            <CreditCard className="h-6 w-6 text-red-600 mr-2" />
            <h2 className="text-xl font-bold">Buses con Deuda Pendiente</h2>
            <span className="ml-3 bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
              {buses.filter(b => (b.credito_usado || 0) > 0).length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {buses
              .filter(b => (b.credito_usado || 0) > 0)
              .sort((a, b) => (b.credito_usado || 0) - (a.credito_usado || 0))
              .map((bus) => (
                <div
                  key={bus.id}
                  onClick={() => navigate(`/buses/${bus.id}`)}
                  className="bg-white rounded-lg shadow-md border-l-4 border-red-500 p-4 cursor-pointer hover:shadow-lg hover:bg-red-50 transition-all"
                  title="Ir al bus para pagar la deuda"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Bus className="h-5 w-5 text-blue-600 mr-2" />
                      <span className="font-bold text-gray-900">Padrón {bus.padron}</span>
                    </div>
                    <span className="text-red-600 font-bold">S/ {(bus.credito_usado || 0).toFixed(2)}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{bus.marca} {bus.modelo}</p>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Alerta de pago de mantenimiento pendiente (cuota fija por bus, dias 15 y fin de mes) */}
      {pagosMantPendientes.pendientes.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center">
            <Wrench className="h-6 w-6 text-orange-600 mr-2" />
            <h2 className="text-xl font-bold">Pago de Mantenimiento Pendiente</h2>
            <span className="ml-3 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
              {pagosMantPendientes.pendientes.length}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            Periodo {pagosMantPendientes.periodo ? etiquetaPeriodoDash(pagosMantPendientes.periodo) : ''} — S/ {pagosMantPendientes.monto.toFixed(2)} por bus
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pagosMantPendientes.pendientes.map((bus: any) => (
              <div key={bus.id} className="bg-white rounded-lg shadow-md border-l-4 border-orange-500 p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <Bus className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="font-bold text-gray-900">Padrón {bus.padron}</span>
                </div>
                <button
                  onClick={() => window.location.href = '/pago-mantenimiento'}
                  className="text-xs px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  Registrar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alertas de Mantenimiento */}
      {/* Alertas de Mantenimiento - Cards Grid */}
      {alertasMantenimiento.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
              <h2 className="text-xl font-bold">Alertas de Mantenimiento Programados</h2>
              <span className="ml-3 bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                {alertasMantenimiento.length}
              </span>
            </div>
          </div>

          {/* Grid de Alertas como Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {alertasMantenimiento.slice(0, 6).map((alerta) => (
              <div
                key={alerta.id}
                className={`bg-white rounded-lg shadow-md border-l-4 hover:shadow-lg transition-shadow ${
                  alerta.prioridad === 'critica' ? 'border-red-500' :
                  alerta.prioridad === 'alta' ? 'border-orange-500' :
                  'border-yellow-500'
                }`}
              >
                <div className="p-4">
                  {/* Header del Card */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <Wrench className={`h-5 w-5 mr-2 ${
                        alerta.prioridad === 'critica' ? 'text-red-600' :
                        alerta.prioridad === 'alta' ? 'text-orange-600' :
                        'text-yellow-600'
                      }`} />
                      <span className="font-bold text-gray-900 text-sm">{alerta.tipo}</span>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      alerta.prioridad === 'critica' ? 'bg-red-100 text-red-800' :
                      alerta.prioridad === 'alta' ? 'bg-orange-100 text-orange-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {alerta.prioridad.toUpperCase()}
                    </span>
                  </div>

                  {/* Info del Bus */}
                  <div className="mb-3 space-y-1">
                    <div className="flex items-center text-sm">
                      <Bus className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="font-semibold text-gray-900">
                        Padrón {alerta.busPadron}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 ml-6">
                      {alerta.bus} - {alerta.busModelo}
                    </p>
                  </div>

                  {/* Detalles */}
                  <div className="mb-3 space-y-1 text-xs text-gray-600">
                    <p><strong>Categoría:</strong> {alerta.categoria}</p>
                    <p><strong>Fecha:</strong> {new Date(alerta.fechaProgramada).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>

                  {/* Mensaje de Alerta */}
                  <p className={`text-xs font-medium mb-3 ${
                    alerta.prioridad === 'critica' ? 'text-red-700' :
                    alerta.prioridad === 'alta' ? 'text-orange-700' :
                    'text-yellow-700'
                  }`}>
                    {alerta.mensaje}
                  </p>

                  {/* Descripción (si existe) */}
                  {alerta.descripcion && (
                    <p className="text-xs text-gray-500 mb-3 line-clamp-2">
                      {alerta.descripcion}
                    </p>
                  )}

                  {/* Botón Empezar */}
                  <button
                    onClick={() => iniciarMantenimiento(alerta.id)}
                    className={`w-full py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
                      alerta.prioridad === 'critica' 
                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                        : alerta.prioridad === 'alta'
                        ? 'bg-orange-600 hover:bg-orange-700 text-white'
                        : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                    }`}
                  >
                    ▶ Empezar Mantenimiento
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Ver todas las alertas */}
          {alertasMantenimiento.length > 6 && (
            <div className="text-center pt-2">
              <button
                onClick={() => window.location.href = '/stock'}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Ver todas las alertas ({alertasMantenimiento.length})
              </button>
            </div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Total Ventas"
          value={`S/ ${stats.totalVentas.toFixed(2)}`}
          icon={DollarSign}
          color="text-green-600"
          subtitle="Todas las ventas registradas"
        />
        <StatCard
          title="Buses Activos"
          value={stats.busesActivos}
          icon={Bus}
          color="text-blue-600"
          subtitle="Buses en servicio"
        />
        <StatCard
          title="En Mantenimiento"
          value={stats.alertasCriticas}
          icon={AlertTriangle}
          color="text-red-600"
          subtitle="Alertas críticas de mantenimiento"
        />
        <StatCard
          title="Mecánicos Disponibles"
          value={stats.mecanicosDisponibles}
          icon={Users}
          color="text-purple-600"
          subtitle="Personal listo para trabajar"
        />
        <StatCard
          title="Ventas Pendientes"
          value={stats.ventasPendientes}
          icon={Clock}
          color="text-yellow-600"
          subtitle="Órdenes por completar"
        />
        <StatCard
          title="Ventas Completadas"
          value={stats.ventasCompletadas}
          icon={CheckCircle}
          color="text-green-600"
          subtitle="Órdenes finalizadas"
        />
        <StatCard
          title="Asistencia Hoy"
          value={`${stats.asistenciaHoy}/${stats.mecanicosDisponibles}`}
          icon={Users}
          color="text-teal-600"
          subtitle={`${stats.asistenciaHoy} de ${stats.mecanicosDisponibles} mecánicos marcaron asistencia`}
        />
        <StatCard
          title="% Asistencia"
          value={`${stats.porcentajeAsistencia}%`}
          icon={CheckCircle}
          color="text-indigo-600"
          subtitle={`Porcentaje de asistencia del día de hoy`}
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Acciones Rápidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button 
            onClick={() => window.location.href = '/ventas'}
            className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <BarChart3 className="h-6 w-6 text-blue-600 mb-2" />
            <p className="font-medium">Nueva Venta</p>
          </button>
          <button 
            onClick={() => window.location.href = '/stock'}
            className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
          >
            <Package className="h-6 w-6 text-green-600 mb-2" />
            <p className="font-medium">Gestionar Stock</p>
          </button>
          <button 
            onClick={() => window.location.href = '/buses'}
            className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
          >
            <Bus className="h-6 w-6 text-purple-600 mb-2" />
            <p className="font-medium">Ver Buses</p>
          </button>
          <button 
            onClick={() => window.location.href = '/reportes'}
            className="p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
          >
            <TrendingUp className="h-6 w-6 text-orange-600 mb-2" />
            <p className="font-medium">Reportes</p>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Ventas Recientes</h2>
        <div className="space-y-3">
          {ventasRecientes.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No hay ventas recientes</p>
          ) : (
            ventasRecientes.map((venta) => (
              <div key={venta.id} className="flex items-center justify-between py-2 border-b">
                <div>
                  <p className="font-medium">Venta #{venta.numero} - {venta.cliente_nombre || 'Cliente'}</p>
                  <p className="text-sm text-gray-500">{new Date(venta.created_at).toLocaleDateString('es-PE')}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-green-600">S/ {(venta.total || 0).toFixed(2)}</p>
                  <span className={`text-sm px-2 py-1 rounded ${
                    ventaPagada(venta) ? 'bg-green-100 text-green-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {ventaPagada(venta) ? 'Pagado' : 'Pendiente'}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Productos mas vendidos (desde agosto 2026) */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-1 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-600" />
          Productos Más Vendidos
        </h2>
        <p className="text-sm text-gray-500 mb-4">Desde agosto 2026</p>
        {productosMasVendidos.length === 0 ? (
          <p className="text-gray-500 text-center py-4">Todavía no hay ventas registradas</p>
        ) : (
          <div className="space-y-2">
            {productosMasVendidos.map((p, i) => (
              <div key={p.producto_id || i} className="flex items-center justify-between border-b last:border-b-0 py-2">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-green-100 text-green-700 rounded-full text-xs font-bold">{i + 1}</span>
                  <span className="text-sm">{p.producto_nombre}</span>
                </div>
                <div className="text-right text-sm">
                  <span className="font-semibold">{Number(p.cantidad_total).toFixed(0)} und.</span>
                  <span className="text-gray-500 ml-2">S/ {Number(p.monto_total).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fleet Status */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Estado de la Flota</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {buses.length === 0 ? (
            <p className="text-gray-500 col-span-4 text-center py-4">No hay buses registrados</p>
          ) : (
            buses.map((bus) => (
              <div key={bus.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">Padrón {bus.padron}</h3>
                  <span className={`px-2 py-1 rounded text-xs ${
                    bus.estado === 'activo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {bus.estado}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{bus.placa}</p>
                <p className="text-sm text-gray-600">{bus.marca} {bus.modelo}</p>
                <p className="text-sm text-gray-500">Año: {bus.año}</p>
                <p className="text-sm text-gray-500">KM: {(bus.kilometraje || 0).toLocaleString()}</p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Marca Personal - Información del Desarrollador */}
      <div className="mt-8">
        <MarcaPersonal variant="about" />
      </div>
    </div>
  );
};

export default Dashboard;
